/**
 * Per-activity attendance request workflows (policy-owned).
 * Leave group/type workflows stay in Leave Management.
 * Change Shift / Change WO stay on Shift / Weekly Off settings.
 */

export const ATTENDANCE_ACTIVITY_KEYS = [
  "shortLeave",
  "regularise",
  "od",
  "compOffWork",
  "compOffCredit",
  "compOffAvail",
  "overtime",
  "restrictedHoliday",
];

export const ATTENDANCE_ACTIVITY_LABELS = {
  shortLeave: "Short Leave",
  regularise: "Regularise checks",
  od: "On Duty",
  compOffWork: "Comp Off — prior work approval",
  compOffCredit: "Comp Off — credit",
  compOffAvail: "Comp Off — avail",
  overtime: "Overtime",
  restrictedHoliday: "Restricted Holiday",
  attendanceFinalization: "Attendance Finalization",
};

export const ACTION_LABEL_TO_ACTIVITY = {
  "Short Leave": "shortLeave",
  "Regularise checks": "regularise",
  Regularise: "regularise",
  "On Duty": "od",
  "Comp Off": "compOffWork",
  "Comp Off Work": "compOffWork",
  "Comp Off Credit": "compOffCredit",
  "Comp Off Avail": "compOffAvail",
  Overtime: "overtime",
  "Restricted Holiday": "restrictedHoliday",
  RH: "restrictedHoliday",
  "Attendance Finalization": "attendanceFinalization",
  "Attendance Lock": "attendanceFinalization",
  "Attendance Unlock": "attendanceFinalization",
};

export function defaultActivityWorkflow(key) {
  return {
    approvalMethod: key === "attendanceFinalization" ? "passkey" : "workflow",
    needWorkflowSteps: false,
    reminderDays: 3,
    workflowParallelApproval: false,
    workflowEscalationAfterDays: 3,
    workflowAutoApprovalIfNoAction: false,
    workflowRejectionWithReason: true,
    workflowReinitiationAllowed: true,
    workflowSteps: [{ role: "reporting_manager", slaDays: 3, notifyOnly: false }],
    cancellationWorkflowSteps: [{ role: "reporting_manager", slaDays: 3, notifyOnly: false }],
  };
}

export function defaultActivityWorkflows() {
  return ATTENDANCE_ACTIVITY_KEYS.reduce((acc, key) => {
    const base = defaultActivityWorkflow(key);
    acc[key] = {
      ...base,
      workflowSteps: [...base.workflowSteps],
      cancellationWorkflowSteps: [...base.cancellationWorkflowSteps],
    };
    return acc;
  }, {});
}

function normalizeSteps(steps, fallback) {
  const list = Array.isArray(steps) ? steps : [];
  if (!list.length) return fallback;
  return list.map((step) => ({
    role: step.role || "reporting_manager",
    slaDays: Number(step.slaDays) || 3,
    notifyOnly: !!step.notifyOnly,
  }));
}

export function normalizeActivityWorkflow(raw, key) {
  const base = defaultActivityWorkflow(key);
  const merged = { ...base, ...(raw && typeof raw === "object" ? raw : {}) };
  if (merged.approvalMethod === "on_instance") {
    merged.approvalMethod = "auto_approved";
  }
  if (key === "attendanceFinalization") {
    if (!["auto_approved", "workflow", "passkey"].includes(merged.approvalMethod)) {
      merged.approvalMethod = "passkey";
    }
  } else {
    if (!["auto_approved", "workflow"].includes(merged.approvalMethod)) {
      merged.approvalMethod = "workflow";
    }
  }
  merged.workflowSteps = normalizeSteps(merged.workflowSteps, base.workflowSteps);
  merged.cancellationWorkflowSteps = normalizeSteps(
    merged.cancellationWorkflowSteps,
    base.cancellationWorkflowSteps
  );
  return merged;
}

export function normalizeActivityWorkflows(raw) {
  const defaults = defaultActivityWorkflows();
  const src = raw && typeof raw === "object" ? raw : {};
  const out = {};
  ATTENDANCE_ACTIVITY_KEYS.forEach((key) => {
    out[key] = normalizeActivityWorkflow(src[key] || defaults[key], key);
  });
  return out;
}

export function resolveActivityWorkflow(policy, activityKey) {
  const all = normalizeActivityWorkflows(policy?.activityWorkflows);
  return all[activityKey] || defaultActivityWorkflow(activityKey);
}

export function activityKeyFromActionLabel(actionLabel) {
  const raw = String(actionLabel || "").trim();
  const parent = raw.endsWith(" cancellation")
    ? raw.slice(0, -" cancellation".length)
    : raw;
  return ACTION_LABEL_TO_ACTIVITY[parent] || ACTION_LABEL_TO_ACTIVITY[raw] || null;
}

export function isActivityAutoApproved(workflow) {
  return String(workflow?.approvalMethod || "") === "auto_approved";
}

export function isActivityPasskey(workflow) {
  return String(workflow?.approvalMethod || "") === "passkey";
}

export function resolveActivityPendingWith(workflow) {
  if (isActivityAutoApproved(workflow)) return "Auto";
  const steps = Array.isArray(workflow?.workflowSteps) ? workflow.workflowSteps : [];
  const first = steps.find((s) => s && !s.notifyOnly);
  const role = first?.role || "reporting_manager";
  const key = String(role).toUpperCase().replace(/[\s-]+/g, "_");
  if (
    key === "MANAGER" ||
    key === "REPORTING_MANAGER" ||
    key === "REPORTINGMANAGER" ||
    key === "RM"
  ) {
    return "Reporting Manager";
  }
  if (key === "HR" || key.includes("HUMAN")) return "HR";
  if (key === "ADMIN") return "Admin";
  return String(role)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
